import React, { useState, useEffect, useCallback } from 'react';
import { API, C } from '../../constants';
import { formatDur } from '../../utils';

export default function AdminStatsTab({ token }) {
  const [stats, setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const [jobs, setJobs]     = useState({ conversion: [], hls: [] });

  const load = useCallback(() => {
    const headers = { Authorization: `Bearer ${token}` };
    const get = url => fetch(url, { headers }).then(r => { if (!r.ok) throw new Error(); return r.json(); });
    setLoading(true); setError('');
    get(`${API}/admin/stats`)
      .then(d => setStats(d))
      .catch(() => setError('Error cargando estadísticas'))
      .finally(() => setLoading(false));
    // Solo interesa lo que no terminó bien: pendientes, en proceso y con error.
    const notDone = d => (Array.isArray(d) ? d : []).filter(j => j.status !== 'done');
    Promise.all([
      get(`${API}/admin/conversion-queue`).then(notDone).catch(() => []),
      get(`${API}/admin/transcode-queue`).then(notDone).catch(() => []),
    ]).then(([conversion, hls]) => setJobs({ conversion, hls }));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading && !stats) return <p style={{ color:C.textMuted, fontSize:'14px', padding:'20px 0' }}>Cargando estadísticas...</p>;
  if (error && !stats) return (
    <div>
      <p style={{ color:C.danger, fontSize:'14px', marginBottom:'10px' }}>{error}</p>
      <button onClick={load} style={{ padding:'7px 14px', background:C.accentDim, border:`1px solid ${C.accentBorder}`, color:C.accentSoft, borderRadius:'8px', cursor:'pointer', fontSize:'12px' }}>Reintentar</button>
    </div>
  );
  if (!stats)  return null;

  const card = (label, value, sub, accent) => (
    <div style={{ background:C.bgCard, border:`1px solid ${C.border}`, borderRadius:'12px', padding:'18px 22px', display:'flex', flexDirection:'column', gap:'6px' }}>
      <p style={{ color:C.textMuted, fontSize:'11px', textTransform:'uppercase', letterSpacing:'1px', fontFamily:'Inter, sans-serif' }}>{label}</p>
      <p style={{ color: accent || C.text, fontSize:'28px', fontWeight:'700', fontFamily:'Inter, sans-serif', lineHeight:1 }}>{value}</p>
      {sub && <p style={{ color:C.textDim, fontSize:'11px' }}>{sub}</p>}
    </div>
  );

  const disk = stats.disk || {};
  // El backend incluye usuarios sin reproducciones (LEFT JOIN); no aportan al ranking.
  const topUsers = (stats.top_users || []).filter(u => u.titles > 0);

  const statusColor = st => st === 'error' ? C.danger : st === 'processing' ? C.online : C.gold;
  const statusLabel = st => ({ error:'Error', processing:'Procesando', pending:'En cola' }[st] || st);
  const jobList = (title, list) => list.length > 0 && (
    <>
      <h3 style={{ color:C.text, fontSize:'14px', fontWeight:'600', marginBottom:'12px', fontFamily:'Inter, sans-serif' }}>{title} ({list.length})</h3>
      <div style={{ background:C.bgCard, border:`1px solid ${C.border}`, borderRadius:'12px', overflow:'hidden', marginBottom:'28px' }}>
        {list.map((j, i) => (
          <div key={j.filename} style={{ padding:'10px 16px', borderBottom: i < list.length - 1 ? `1px solid ${C.border}` : 'none', display:'flex', alignItems:'center', gap:'12px' }}>
            <span style={{ color:statusColor(j.status), fontSize:'11px', fontWeight:'600', minWidth:'78px', fontFamily:'Inter, sans-serif' }}>{statusLabel(j.status)}</span>
            <div style={{ flex:1, minWidth:0 }}>
              <p style={{ color:C.text, fontSize:'12px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontFamily:'Inter, sans-serif' }}>{j.filename}</p>
              {j.error && <p title={j.error} style={{ color:C.textDim, fontSize:'10px', marginTop:'2px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{j.error}</p>}
            </div>
            {j.attempts > 0 && <span style={{ color:C.textDim, fontSize:'10px', whiteSpace:'nowrap' }}>{j.attempts} {j.attempts === 1 ? 'intento' : 'intentos'}</span>}
          </div>
        ))}
      </div>
    </>
  );

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'18px' }}>
        <h3 style={{ color:C.text, fontSize:'14px', fontWeight:'600', fontFamily:'Inter, sans-serif' }}>Resumen</h3>
        <button onClick={load} disabled={loading} style={{ padding:'6px 12px', background:C.accentDim, border:`1px solid ${C.accentBorder}`, color:C.accentSoft, borderRadius:'8px', cursor: loading ? 'not-allowed' : 'pointer', fontSize:'12px' }}>{loading ? '🔄 Actualizando...' : '🔄 Actualizar'}</button>
      </div>
      {error && <p style={{ color:C.danger, fontSize:'12px', marginBottom:'12px' }}>{error}</p>}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(150px, 1fr))', gap:'12px', marginBottom:'28px' }}>
        {/* Los nombres deben coincidir con el struct Stats de handleAdminStats. */}
        {card('Usuarios', stats.total_users ?? '–', 'sin contar admins', C.accentSoft)}
        {card('Activos 24h', stats.active_today ?? '–', null, C.online)}
        {card('Películas', stats.total_movies ?? '–', null, C.tmdb)}
        {card('Series', stats.total_series ?? '–', null, C.tmdb)}
        {card('Episodios', stats.total_episodes ?? '–', null, C.tmdb)}
      </div>

      <h3 style={{ color:C.text, fontSize:'14px', fontWeight:'600', marginBottom:'12px', fontFamily:'Inter, sans-serif' }}>Almacenamiento</h3>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(150px, 1fr))', gap:'12px', marginBottom:'28px' }}>
        {card('Películas', `${(disk.movies_gb||0).toFixed(1)} GB`, null, C.gold)}
        {card('Series', `${(disk.series_gb||0).toFixed(1)} GB`, null, C.gold)}
        {card('HLS', `${(disk.hls_gb||0).toFixed(1)} GB`, 'streams adaptativos', C.textMuted)}
        {card('Miniaturas', `${(disk.thumbs_gb||0).toFixed(1)} GB`, null, C.textMuted)}
      </div>

      {jobList('Conversión a MP4 pendiente', jobs.conversion)}
      {jobList('Generación HLS pendiente', jobs.hls)}

      {Array.isArray(stats.top_watched) && stats.top_watched.length > 0 && (
        <>
          <h3 style={{ color:C.text, fontSize:'14px', fontWeight:'600', marginBottom:'12px', fontFamily:'Inter, sans-serif' }}>Más visto</h3>
          <div style={{ background:C.bgCard, border:`1px solid ${C.border}`, borderRadius:'12px', overflow:'hidden', marginBottom:'28px' }}>
            {stats.top_watched.map((item, i) => (
              <div key={i} style={{ padding:'11px 16px', borderBottom: i < stats.top_watched.length - 1 ? `1px solid ${C.border}` : 'none', display:'flex', alignItems:'center', gap:'12px' }}>
                <span style={{ color:C.accentSoft, fontSize:'13px', fontWeight:'700', minWidth:'20px', fontFamily:'Inter, sans-serif' }}>{i + 1}</span>
                <span style={{ color:C.text, fontSize:'13px', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontFamily:'Inter, sans-serif' }}>{item.title || item.file}</span>
                <span style={{ color:C.textMuted, fontSize:'11px', whiteSpace:'nowrap', fontFamily:'Inter, sans-serif' }}>{item.watch_count} {item.watch_count === 1 ? 'espectador' : 'espectadores'} · {formatDur(item.minutes_sum * 60) || '0m'}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {topUsers.length > 0 && (
        <>
          <h3 style={{ color:C.text, fontSize:'14px', fontWeight:'600', marginBottom:'12px', fontFamily:'Inter, sans-serif' }}>Usuarios más activos</h3>
          <div style={{ background:C.bgCard, border:`1px solid ${C.border}`, borderRadius:'12px', overflow:'hidden' }}>
            {topUsers.map((u, i) => (
              <div key={i} style={{ padding:'11px 16px', borderBottom: i < topUsers.length - 1 ? `1px solid ${C.border}` : 'none', display:'flex', alignItems:'center', gap:'12px' }}>
                <span style={{ color:C.accentSoft, fontSize:'13px', fontWeight:'700', minWidth:'20px', fontFamily:'Inter, sans-serif' }}>{i + 1}</span>
                <span style={{ color:C.text, fontSize:'13px', flex:1, fontFamily:'Inter, sans-serif' }}>{u.name}</span>
                <span style={{ color:C.textMuted, fontSize:'11px', fontFamily:'Inter, sans-serif' }}>{u.titles} {u.titles === 1 ? 'título' : 'títulos'} · {formatDur(u.minutes * 60) || '0m'}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
